import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import Dashboard from './pages/Dashboard';
import TodoList from './pages/TodoList';
import Analytics from './pages/Analytics';
import Settings from './pages/Settings';
import Sidebar from './components/Sidebar';
import Header from './components/Header';

import { categoriesAPI, tagsAPI, userAPI } from './utils/api';

import './styles/App.css';

function App() {
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const [categoriesRes, tagsRes, userRes] = await Promise.all([
        categoriesAPI.getAll(),
        tagsAPI.getAll(),
        userAPI.getProfile()
      ]);

      setCategories(categoriesRes.data);
      setTags(tagsRes.data);
      setUserProfile(userRes.data);
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshCategories = async () => {
    try {
      const res = await categoriesAPI.getAll();
      setCategories(res.data);
    } catch (error) {
      console.error('Error refreshing categories:', error);
    }
  };

  const refreshTags = async () => {
    try {
      const res = await tagsAPI.getAll();
      setTags(res.data);
    } catch (error) {
      console.error('Error refreshing tags:', error);
    }
  };

  const refreshUserProfile = async () => {
    try {
      const res = await userAPI.getProfile();
      setUserProfile(res.data);
    } catch (error) {
      console.error('Error refreshing user profile:', error);
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading your productivity hub...</p>
      </div>
    );
  }

  return (
    <Router>
      <div className="app">
        <Sidebar 
          isOpen={sidebarOpen} 
          onToggle={() => setSidebarOpen(!sidebarOpen)}
          categories={categories}
          userProfile={userProfile}
        />
        
        <div className={`main-content ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
          <Header 
            onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
            userProfile={userProfile}
          />
          
          <div className="content-wrapper">
            <Routes>
              <Route 
                path="/" 
                element={
                  <Dashboard 
                    categories={categories}
                    tags={tags}
                    userProfile={userProfile}
                  />
                } 
              />
              <Route 
                path="/todos/:view?" 
                element={
                  <TodoList 
                    categories={categories}
                    tags={tags}
                    onCategoryUpdate={refreshCategories}
                    onTagUpdate={refreshTags}
                  />
                } 
              />
              <Route 
                path="/analytics" 
                element={
                  <Analytics 
                    categories={categories}
                    tags={tags}
                  />
                } 
              />
              <Route 
                path="/settings" 
                element={
                  <Settings 
                    categories={categories}
                    tags={tags}
                    userProfile={userProfile}
                    onCategoryUpdate={refreshCategories}
                    onTagUpdate={refreshTags}
                    onUserUpdate={refreshUserProfile}
                  />
                } 
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </div>

        <ToastContainer
          position="bottom-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    </Router>
  );
}

export default App;
